import { Language } from '../domain/language';
import { ILanguageTranslations } from '../domain/translation.types';

/**
 * Local translation dictionaries — the offline DEFAULT source of truth and the
 * merge/fallback layer for the HTTP repository (API values WIN, the
 * dictionary fills any gap; never duplicated, one value per key).
 *
 * KEY CONVENTION: every key is flat and prefixed `reference_portal_*`
 * (snake_case). No other naming convention is used anywhere in /v2 — the API
 * is expected to return the same `reference_portal_*` keys so the merge is
 * 1:1.
 *
 * Covers the static UI chrome for the Splash + Home + shell surfaces. Dynamic
 * content (greeting name, cards, sessions) is DATA and comes from the Home
 * repo.
 *
 * ── LAZY PER-LANGUAGE SPLIT (rev. 9 perf pass) ──────────────────────────────
 * The actual `ar`/`en` data now live in their own chunks
 * (`translations.ar.dictionary.ts` / `translations.en.dictionary.ts`,
 * ~350-375 KB source each) instead of one combined ~812 KB eager import — see
 * PERFORMANCE-AUDIT.md for the full before/after. This module is the loader:
 *
 *   · `preloadLocalTranslations(lang)` — dynamically imports `lang`'s chunk
 *     and caches it. MUST be awaited before the language it targets is read
 *     synchronously anywhere below — both bootstrap entry points
 *     (`src/main.ts` browser-side, `src/main.server.ts` server-side) await
 *     this for the language that will render BEFORE calling
 *     `bootstrapApplication`, which is what keeps `TranslationFacade`'s own
 *     synchronous constructor seed correct with zero visible flash (the
 *     trade-off earlier passes flagged as needing product sign-off before a
 *     naive split — this gates around it instead of accepting it).
 *   · `LOCAL_TRANSLATIONS` — synchronous, always-safe read. A language that
 *     hasn't been preloaded yet reads as `{}` (harmless: every consumer
 *     already guards with `LOCAL_TRANSLATIONS[lang] ?? {}` / `?? {}`-shaped
 *     fallbacks, so this degrades exactly like a slow API response already
 *     does — never a crash, never wrong data, at worst a brief empty map).
 *
 * `TranslationFacade.loadFallback()` preloads the FALLBACK language the same
 * way, in the background, after the primary language is already showing —
 * see its own comment for why that one small async gap is safe to accept.
 */
const resolved: Partial<Record<Language, ILanguageTranslations>> = {};
const pending = new Map<Language, Promise<ILanguageTranslations>>();

function importChunk(lang: Language): Promise<ILanguageTranslations> {
  return lang === Language.En
    ? import('./translations.en.dictionary').then((m) => m.EN)
    : import('./translations.ar.dictionary').then((m) => m.AR);
}

/** Load (and cache) `lang`'s dictionary chunk. Safe to call repeatedly — de-duped. */
export function preloadLocalTranslations(lang: Language): Promise<ILanguageTranslations> {
  const already = resolved[lang];
  if (already) return Promise.resolve(already);

  const inFlight = pending.get(lang);
  if (inFlight) return inFlight;

  const load = importChunk(lang).then((dict) => {
    resolved[lang] = dict;
    pending.delete(lang);
    return dict;
  });
  pending.set(lang, load);
  return load;
}

/**
 * Synchronous read of whatever has been preloaded so far. `Partial` (not
 * `Record`) on purpose — see the module comment above for why a not-yet-loaded
 * language reading as `undefined` is expected, not a bug.
 */
export const LOCAL_TRANSLATIONS: Partial<Record<Language, ILanguageTranslations>> = resolved;
